import { PayloadAction } from '@reduxjs/toolkit';
import { HowlOptions } from 'howler';
import { Action } from 'redux';
import { combineEpics, StateObservable } from 'redux-observable';
import {
  filter, Observable, map,
  of,
  concatMap,
  concat,
  interval,
  take,
  takeUntil,
  flatMap,
  timer,
  switchMap,
  ignoreElements,
  tap,
} from 'rxjs';

import { FADE_BONUS_CONTAINER_ANIMATION_DURATION } from '@ezugi/bootstrap/src/views/GameBonus/style.css';
import { BonusWheelOperatorErrorGEMessage, BonusWheelStartGEMessage } from '@ezugi/bootstrap/src/views/GameBonus/types';
import {
  BACCARAT_ID,
  BLACKJACK_ID,
  BONUS_GAME_ID,
  BonusMessage,
  BonusStatus,
  ROULETTE_AUTOMATED_ID,
  ROULETTE_ID,
} from '@ezugi/constants';
import bonusSprite from '@ezugi/static/sounds/bonus_sprite.json';
import { SocketMessagePayload } from '@ezugi/types';

import {
  bonusActions, dialogActions, mediaActions, notificationActions, preloaderActions, socketActions,
} from '../../actions';
import { BONUS_WHEEL_INSUFFICIENT_BALANCE_DIALOG } from '../../constants/dialog';
import { BonusRejectionActionPayload } from '../../reducers/bonus';
import {
  clientIdSelector,
  gameIdSelector,
  hasInitializedBalanceSelector,
  isVideoStreamSoundMutedSelector,
  userBalanceSelector,
} from '../../selectors';
import {
  bonusRoundIdSelector,
  bonusSelectedChipSelector,
  isBonusRejectedSelector,
  isBonusVisibleSelector,
  wasStreamSoundMutedBeforeBonusSelector,
} from '../../selectors/bonus';
import { createAndCacheSound } from '../sound/soundCache';

import type { RootState } from '../../types';

const { socket } = socketActions;
const { bonus } = bonusActions;
const { videoStreamSound } = mediaActions;
const { notification } = notificationActions;
const { dialog } = dialogActions;

const displayAndHideBonusBasedOnEngineTimersAndRejectionEpic = (action$:
  Observable<PayloadAction<BonusWheelStartGEMessage>>, state$: StateObservable<RootState>) => action$.pipe(
  filter((action) => socket.message.match(action)),
  map(({ payload }) => payload),
  filter((payload) => payload.MessageType === BonusMessage.BonusWheelStart),
  concatMap((payload) => {
    const duration = payload.SecondsLeft * 1000;

    const hasInitializedBalance = hasInitializedBalanceSelector(state$.value);
    const balance = hasInitializedBalance ? userBalanceSelector(state$.value) : payload.balance;
    const chips = payload.chips;
    const smallestChipAmount = Math.min(...chips.map(({ value }) => value));

    if (balance < smallestChipAmount) {
      return concat(of(dialog.add({ name: BONUS_WHEEL_INSUFFICIENT_BALANCE_DIALOG })));
    }

    const selectedChip = chips.reduce(
      (acc, curr) => (curr.value > acc.value && balance >= curr.value ? curr : acc),
      chips[0]
    );

    const isVideoStreamSoundMuted = isVideoStreamSoundMutedSelector(state$.value);

    return concat(
      isVideoStreamSoundMuted ? of(
        bonus.setWasStreamSoundMutedBeforeBonus(true)
      ) : of(
        videoStreamSound.set(false),
        bonus.setWasStreamSoundMutedBeforeBonus(false)
      ),
      of(bonus.init({
        status: BonusStatus.ClaimYourBonus,
        visible: true,
        multiplierPrize: payload.Multiplier,
        wonAmount: 0,
        hasOperatorError: false,
        timers: {
          duration,
          timeLeft: duration,
        },
        chips,
        selectedChip,
        roundId: payload.roundId,
      })),
      interval(1000).pipe(
        take(
          duration / 1000
        ),
        map((i) => bonus.setTimers({ timeLeft: duration - 1000 * (i + 1) })),
        takeUntil(action$.pipe(
          filter((action) => (bonus.setStatus.match(action) && action.payload !== BonusStatus.ClaimYourBonus)
          || (bonus.setRejected.match(action) && action.payload.rejected === true)),
        ))
      ),
      of(bonus.setRejected({ rejected: true }))
    ).pipe(
      takeUntil(action$.pipe(
        filter((action) => (bonus.setStatus.match(action) && action.payload !== BonusStatus.ClaimYourBonus)
          || (bonus.setRejected.match(action) && action.payload.rejected === true)),
      ))
    );
  })
);

const bonusRejectionEpic = (
  action$: Observable<PayloadAction<BonusRejectionActionPayload>>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter((action) => bonus.setRejected.match(action) && action.payload.rejected === true),
  map(({ payload }) => socket.send({
    MessageType: BonusMessage.BonusWheelCancelled,
    ClientId: clientIdSelector(state$.value),
    gameType: BONUS_GAME_ID,
    userCancelled: Boolean(payload.userCancelled),
  }))
);

const bonusAcceptedEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter((action) => bonus.setStatus.match(action) && action.payload === BonusStatus.Spin),
  map(() => socket.send({
    MessageType: BonusMessage.BonusWheelAccepted,
    ClientId: clientIdSelector(state$.value),
    amount: bonusSelectedChipSelector(state$.value)?.value,
    gameType: BONUS_GAME_ID,
  }))
);

const bonusWheelFinishedEpic = (action$: Observable<SocketMessagePayload>) => action$.pipe(
  filter(socket.message.match),
  map(({ payload }) => payload),
  filter((payload) => payload.MessageType === BonusMessage.BonusWheelFinished),
  map(({ payout }) => bonus.setWonAmount(payout))
);

const unmountBonusEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter((action) => (bonus.setVisibility.match(action) && action.payload === false)),
  switchMap(() => timer(FADE_BONUS_CONTAINER_ANIMATION_DURATION * 1000)),
  flatMap(() => {
    const isBonusRejected = isBonusRejectedSelector(state$.value);

    return isBonusRejected
      ? of(bonus.setStatus(BonusStatus.WaitingForTrigger), bonus.setRejected({ rejected: false }))
      : of(bonus.setStatus(BonusStatus.WaitingForTrigger));
  })
);

const hideBonusOnRejectionEpic = (action$: Observable<Action>) => action$.pipe(
  filter((action) => bonus.setRejected.match(action) && action.payload.rejected === true),
  map(() => bonus.setVisibility(false))
);

const unmuteVideoStreamSoundWhenBonusIsFinishedEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter((action) => bonus.setStatus.match(action)
  && action.payload === BonusStatus.WaitingForTrigger
  && !wasStreamSoundMutedBeforeBonusSelector(state$.value)),
  map(() => videoStreamSound.set(true))
);

const bonusOperatorErrorEpic = (
  action$: Observable<PayloadAction<BonusWheelOperatorErrorGEMessage>>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter((action) => socket.message.match(action)),
  filter(({ payload }) => payload.MessageType === BonusMessage.BonusWheelOperatorError),
  filter(({ payload }) => payload.roundId === bonusRoundIdSelector(state$.value)
  && isBonusVisibleSelector(state$.value)),
  flatMap(() => of(
    notification.add({
      message: 'notifications.operator_error_code_1',
    }),
    bonus.setVisibility(false),
    bonus.setHasOperatorError(true)
  ))
);

const gameIdsWithBonusWheel = [ ROULETTE_ID, ROULETTE_AUTOMATED_ID, BACCARAT_ID, BLACKJACK_ID ];
// Preload Game Bonus assets only after it no longer impacts game load
const preloadBonusAssets = (
  action$: Observable<PayloadAction<SocketMessagePayload>>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(preloaderActions.preloader.hide.match),
  filter(() => gameIdsWithBonusWheel.includes(Number(gameIdSelector(state$.value)))),
  tap(() => {
    // Load bonus JS
    import('../../../views/GameBonus/GameBonus');

    // Load bonus sound sprite
    createAndCacheSound({
      spriteName: 'bonus_sprite',
      spriteJson: bonusSprite as unknown as HowlOptions,
      sources: bonusSprite.src.map((name) => `${`${import.meta.env.VITE_STATIC_URL }/sounds`}/${name}`),
    });

    // Load bonus fonts
    [ 'Sancreek', 'Spicy Rice' ].forEach((font) => {
      document.fonts.load(`1em "${font}"`);
    });
  }),
  ignoreElements()
);

export default combineEpics(
  // @ts-expect-error
  displayAndHideBonusBasedOnEngineTimersAndRejectionEpic,
  bonusRejectionEpic,
  bonusAcceptedEpic,
  bonusWheelFinishedEpic,
  unmountBonusEpic,
  hideBonusOnRejectionEpic,
  bonusOperatorErrorEpic,
  unmuteVideoStreamSoundWhenBonusIsFinishedEpic,
  preloadBonusAssets,
);
